import { useState, useRef } from 'react';
import { FiDownload, FiFileText } from 'react-icons/fi';
import api from '../../utils/api';
import { exportToExcel } from '../../utils/exportUtils';

export default function Reports() {
  const [reportType, setReportType] = useState('monthly');
  const [period, setPeriod] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const reportRef = useRef();

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = period ? `?period=${encodeURIComponent(period)}&reportType=${reportType}` : '';
      const [dash, completion, satisfaction, dept, staffNE, traineeSummary, traineeTracker, ojtStats] = await Promise.all([
        api.get(`/reports/dashboard${params}`),
        api.get(`/reports/course-completion${params}`),
        api.get(`/reports/satisfaction${params}`),
        api.get(`/reports/department-breakdown${params}`),
        api.get('/staff/near-expiry'),
        api.get('/trainees/stats/summary'),
        api.get('/trainees/stats/tracker'),
        api.get(`/ojt/stats${params}`),
      ]);
      setReportData({ dash, completion, satisfaction, dept, staffNE, traineeSummary, traineeTracker, ojtStats });
    } catch (err) {
      alert('Failed to generate report: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    const el = reportRef.current;
    if (!el) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      // Fix width to A4-proportional size for consistent PDF rendering
      const origWidth = el.style.width;
      const origMaxWidth = el.style.maxWidth;
      el.style.width = '800px';
      el.style.maxWidth = '800px';

      const canvas = await html2canvas(el, {
        scale: 1.5,
        backgroundColor: '#ffffff',
        useCORS: true,
        windowWidth: 800,
      });

      // Restore original styles
      el.style.width = origWidth;
      el.style.maxWidth = origMaxWidth;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Calculate how many pixels of the canvas fit per PDF page
      const pxPerPage = (usableHeight / imgWidth) * canvas.width;
      const totalPages = Math.ceil(canvas.height / pxPerPage);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const srcY = page * pxPerPage;
        const sliceH = Math.min(pxPerPage, canvas.height - srcY);
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = canvas.width;
        tmpCanvas.height = sliceH;
        const ctx = tmpCanvas.getContext('2d');
        ctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        const sliceData = tmpCanvas.toDataURL('image/jpeg', 0.92);
        const sliceImgH = (sliceH * imgWidth) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgWidth, sliceImgH);
      }

      pdf.save(`NEOM_ATA_Executive_${reportType}_report.pdf`);
    } catch (err) {
      alert('PDF export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const exportNearExpiryExcel = () => {
    if (!reportData?.staffNE?.staff) return;
    const rows = reportData.staffNE.staff.map((s) => ({
      'Employee ID': s.employee_id,
      'Name': s.staff_name,
      'Department': s.department,
      'Near Expiry Certs': (s.near_expiry_certs || []).join(', '),
      'BLS Expiry': s.bls_expiry_date,
      'ACLS Expiry': s.acls_expiry_date,
      'ATLS Expiry': s.atls_expiry_date,
      'NRP Expiry': s.nrp_expiry_date,
    }));
    exportToExcel(rows, 'near_expiry_by_department');
  };

  const d = reportData?.dash;
  const summary = reportData?.completion?.summary || [];
  const deptStaff = reportData?.dept?.staff_by_department || [];
  const grSatisfaction = reportData?.satisfaction?.grand_round_satisfaction || [];
  const ts = reportData?.traineeSummary;
  const trainees = reportData?.traineeTracker?.trainees || [];
  const ojt = reportData?.ojtStats;

  // Compute completion tiers
  const completionTiers = trainees.reduce(
    (acc, t) => {
      const pct = t.completion_pct || 0;
      if (pct <= 25) acc['0-25%'] += 1;
      else if (pct <= 50) acc['26-50%'] += 1;
      else if (pct <= 75) acc['51-75%'] += 1;
      else acc['76-100%'] += 1;
      return acc;
    },
    { '0-25%': 0, '26-50%': 0, '51-75%': 0, '76-100%': 0 }
  );

  // Computed values
  const totalCourseAttendees = summary.reduce((sum, s) => sum + (s.total_attendees || 0), 0);
  const completionRate = (d?.total_courses > 0)
    ? Math.round(((d?.completed_courses || 0) / d.total_courses) * 100)
    : 0;
  const traineeCompletionRate = trainees.length > 0
    ? Math.round(trainees.filter(t => (t.completion_pct || 0) >= 100).length / trainees.length * 100)
    : 0;
  const avgGRSatisfaction = grSatisfaction.length > 0
    ? (grSatisfaction.reduce((s, g) => s + (Number(g.avg_q1) + Number(g.avg_q2) + Number(g.avg_q3) + Number(g.avg_q4)) / 4, 0) / grSatisfaction.length).toFixed(1)
    : 'N/A';

  // Build the summary paragraph dynamically
  const buildSummaryParagraph = () => {
    if (!d) return '';
    const periodLabel = period || 'the reporting period';
    const parts = [];
    parts.push(`During ${periodLabel}, the Academic & Training Affairs department managed a total of ${d.total_staff || 0} staff members across ${deptStaff.length} departments.`);
    parts.push(`${d.total_courses || 0} life support courses were conducted with a ${completionRate}% completion rate, recording ${totalCourseAttendees} total attendees.`);
    if ((d.staff_with_near_expiry_certs || 0) > 0 || (d.staff_with_expired_certs || 0) > 0) {
      parts.push(`${d.staff_with_expired_certs || 0} staff members have expired certifications and ${d.staff_with_near_expiry_certs || 0} have certificates approaching expiry, requiring immediate attention.`);
    }
    parts.push(`${d.total_grand_rounds || 0} grand round sessions were held as part of continuing professional development${avgGRSatisfaction !== 'N/A' ? ` with an average satisfaction score of ${avgGRSatisfaction}/5` : ''}.`);
    parts.push(`The post-graduate training program currently has ${ts?.total || 0} trainees enrolled — ${ts?.by_status?.active || 0} active, ${ts?.by_status?.completed || 0} completed, and ${ts?.by_status?.upcoming || 0} upcoming — with ${traineeCompletionRate}% having completed their full process requirements.`);
    if (ojt?.total > 0) {
      parts.push(`On-the-job training recorded ${ojt.total} course${ojt.total !== 1 ? 's' : ''} with a total cost of SAR ${Number(ojt.totalCost || 0).toLocaleString()}, of which ${ojt.oulApproved} received OUL approval.`);
    }
    return parts.join(' ');
  };

  return (
    <div>
      {/* Report Generator */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Generate Report</h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Report Type</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value)} style={{ width: '180px' }}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Period</label>
            <input
              type={reportType === 'yearly' ? 'number' : reportType === 'quarterly' ? 'text' : 'month'}
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              placeholder={reportType === 'quarterly' ? 'e.g., Q1 2026' : reportType === 'yearly' ? '2026' : ''}
              style={{ width: '180px' }}
            />
          </div>
          <button className="btn btn-primary" onClick={generateReport} disabled={loading}>
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Display */}
      {reportData && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', justifyContent: 'flex-end' }}>
            <button style={styles.exportBtn} onClick={exportPDF} disabled={exporting}>
              <FiDownload size={14} /> {exporting ? 'Exporting...' : 'Download PDF'}
            </button>
            <button style={styles.exportBtnOutline} onClick={exportNearExpiryExcel}>
              <FiFileText size={14} /> Export Near Expiry (Excel)
            </button>
          </div>

          <div ref={reportRef} style={styles.reportContainer}>

            {/* ═══════════════ PAGE 1: HEADER + EXECUTIVE OVERVIEW ═══════════════ */}
            <div data-pdf-section="header">
              {/* Header Bar */}
              <div style={styles.headerBar}>
                <div style={styles.headerLogoArea}>
                  <div style={styles.headerIcon}>ATA</div>
                  <div>
                    <div style={styles.headerTitle}>NEOM Hospital</div>
                    <div style={styles.headerSubtitle}>Academic & Training Affairs</div>
                  </div>
                </div>
              </div>

              <div style={styles.reportTitleArea}>
                <h1 style={styles.reportMainTitle}>Executive Summary Report</h1>
                <div style={styles.reportMeta}>
                  <span style={styles.metaBadge}>
                    {reportType.charAt(0).toUpperCase() + reportType.slice(1)} Report
                  </span>
                  {period && <span style={styles.metaBadge}>{period}</span>}
                  <span style={styles.metaBadge}>
                    {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              </div>

              <div style={styles.divider} />

              {/* Summary Paragraph */}
              <div style={styles.summaryBox}>
                <h3 style={styles.summaryTitle}>Overview</h3>
                <p style={styles.summaryText}>{buildSummaryParagraph()}</p>
              </div>

              <div style={styles.divider} />

              {/* KPI Grid */}
              <div style={styles.execSection}>
                <div style={styles.execHeader}>
                  <div style={styles.execNumber}>00</div>
                  <div>
                    <h2 style={styles.execTitle}>Key Performance Indicators</h2>
                    <p style={styles.execDesc}>At-a-glance metrics across all departments</p>
                  </div>
                </div>

                <div style={styles.kpiGrid}>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#6B8063' }}>
                    <div style={styles.kpiValue}>{d?.total_staff || 0}</div>
                    <div style={styles.kpiLabel}>Total Staff</div>
                  </div>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#3498DB' }}>
                    <div style={styles.kpiValue}>{d?.total_courses || 0}</div>
                    <div style={styles.kpiLabel}>Total Courses</div>
                  </div>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#6B8063' }}>
                    <div style={styles.kpiValue}>{completionRate}%</div>
                    <div style={styles.kpiLabel}>Course Completion Rate</div>
                  </div>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#D4A017' }}>
                    <div style={styles.kpiValue}>{d?.total_grand_rounds || 0}</div>
                    <div style={styles.kpiLabel}>Grand Rounds</div>
                  </div>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#9B59B6' }}>
                    <div style={styles.kpiValue}>{ts?.total || 0}</div>
                    <div style={styles.kpiLabel}>Total Trainees</div>
                  </div>
                  <div style={{ ...styles.kpiCard, borderLeftColor: '#C0392B' }}>
                    <div style={styles.kpiValue}>{d?.staff_with_expired_certs || 0}</div>
                    <div style={styles.kpiLabel}>Expired Certificates</div>
                  </div>
                </div>

                <div style={styles.highlightBar}>
                  <div style={styles.highlightItem}>
                    <span style={{ ...styles.highlightDot, backgroundColor: d?.staff_with_near_expiry_certs > 0 ? '#D4A017' : '#6B8063' }} />
                    <span>{d?.staff_with_near_expiry_certs || 0} staff with near-expiry certifications requiring attention</span>
                  </div>
                  <div style={styles.highlightItem}>
                    <span style={{ ...styles.highlightDot, backgroundColor: '#6B8063' }} />
                    <span>{totalCourseAttendees} total course attendees across all sessions</span>
                  </div>
                  <div style={styles.highlightItem}>
                    <span style={{ ...styles.highlightDot, backgroundColor: '#3498DB' }} />
                    <span>{traineeCompletionRate}% of trainees have completed their full process</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ═══════════════ PAGE 2: LIFE SUPPORT SNAPSHOT ═══════════════ */}
            <div data-pdf-section="life-support" style={styles.sectionBlock}>
              <div style={styles.sectionHeader}>
                <div style={styles.sectionIcon}>01</div>
                <div>
                  <h2 style={styles.sectionTitle}>Life Support Courses</h2>
                  <p style={styles.sectionDesc}>Certification tracking, course completion, and staff readiness</p>
                </div>
              </div>

              {/* Snapshot Stats */}
              <div style={styles.snapshotGrid}>
                {[
                  ['Total Staff', d?.total_staff || 0, '#6B8063'],
                  ['Total Courses', d?.total_courses || 0, '#3498DB'],
                  ['Completed Courses', d?.completed_courses || 0, '#6B8063'],
                  ['Total Attendance', d?.total_course_attendance || 0, '#D4A017'],
                  ['Near Expiry', d?.staff_with_near_expiry_certs || 0, '#D4A017'],
                  ['Expired Certs', d?.staff_with_expired_certs || 0, '#C0392B'],
                ].map(([label, val, color]) => (
                  <div key={label} style={styles.snapshotCard}>
                    <div style={{ ...styles.snapshotValue, color }}>{val}</div>
                    <div style={styles.snapshotLabel}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Course Breakdown - compact */}
              <h4 style={styles.tableTitle}>Course Breakdown</h4>
              <div style={styles.courseChips}>
                {summary.length > 0 ? summary.map((s) => (
                  <div key={s.course_name} style={styles.courseChip}>
                    <div style={styles.courseChipName}>{s.course_name}</div>
                    <div style={styles.courseChipStats}>
                      <span>{s.total_courses} sessions</span>
                      <span style={styles.chipDot} />
                      <span>{s.completed} completed</span>
                      <span style={styles.chipDot} />
                      <span>{s.total_attendees} attendees</span>
                    </div>
                  </div>
                )) : (
                  <p style={styles.noData}>No course data available</p>
                )}
              </div>

              {/* Staff by Department - compact cards */}
              <h4 style={styles.tableTitle}>Staff by Department</h4>
              <div style={styles.deptGrid}>
                {deptStaff.length > 0 ? deptStaff.map((dep) => (
                  <div key={dep.department} style={styles.deptCard}>
                    <div style={styles.deptCount}>{dep.staff_count}</div>
                    <div style={styles.deptName}>{dep.department}</div>
                  </div>
                )) : (
                  <p style={styles.noData}>No department data</p>
                )}
              </div>
            </div>

            {/* ═══════════════ PAGE 3: CPD SNAPSHOT ═══════════════ */}
            <div data-pdf-section="cpd" style={styles.sectionBlock}>
              <div style={styles.sectionHeader}>
                <div style={{ ...styles.sectionIcon, backgroundColor: '#3498DB' }}>02</div>
                <div>
                  <h2 style={styles.sectionTitle}>CPD Activities (Grand Rounds)</h2>
                  <p style={styles.sectionDesc}>Continuing professional development sessions and satisfaction</p>
                </div>
              </div>

              <div style={styles.snapshotGrid}>
                {[
                  ['Total Grand Rounds', d?.total_grand_rounds || 0, '#3498DB'],
                  ['Total Attendance', d?.total_grand_round_attendance || 0, '#6B8063'],
                  ['Avg Satisfaction', avgGRSatisfaction !== 'N/A' ? `${avgGRSatisfaction}/5` : 'N/A', '#D4A017'],
                  ['Total Responses', grSatisfaction.reduce((s, g) => s + (g.response_count || 0), 0), '#9B59B6'],
                ].map(([label, val, color]) => (
                  <div key={label} style={styles.snapshotCard}>
                    <div style={{ ...styles.snapshotValue, color }}>{val}</div>
                    <div style={styles.snapshotLabel}>{label}</div>
                  </div>
                ))}
              </div>

              {grSatisfaction.length > 0 ? (
                <>
                  <h4 style={styles.tableTitle}>Satisfaction by Activity Type</h4>
                  <div style={styles.courseChips}>
                    {grSatisfaction.map((g) => {
                      const avg = ((Number(g.avg_q1) + Number(g.avg_q2) + Number(g.avg_q3) + Number(g.avg_q4)) / 4).toFixed(1);
                      return (
                        <div key={g.activity_type} style={styles.courseChip}>
                          <div style={styles.courseChipName}>{g.activity_type}</div>
                          <div style={styles.courseChipStats}>
                            <span>Avg Score: {avg}/5</span>
                            <span style={styles.chipDot} />
                            <span>{Number(g.recommend_pct).toFixed(0)}% recommend</span>
                            <span style={styles.chipDot} />
                            <span>{g.response_count} responses</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={styles.emptyNotice}>No grand round satisfaction data available for this period.</div>
              )}
            </div>

            {/* ═══════════════ PAGE 4: POST GRADUATE SNAPSHOT ═══════════════ */}
            <div data-pdf-section="postgrad" style={styles.sectionBlock}>
              <div style={styles.sectionHeader}>
                <div style={{ ...styles.sectionIcon, backgroundColor: '#9B59B6' }}>03</div>
                <div>
                  <h2 style={styles.sectionTitle}>Training</h2>
                  <p style={styles.sectionDesc}>Trainee enrollment, progress tracking, and process completion</p>
                </div>
              </div>

              <div style={styles.snapshotGrid}>
                {[
                  ['Total Trainees', ts?.total || 0, '#2B362E'],
                  ['Active', ts?.by_status?.active || 0, '#6B8063'],
                  ['Completed', ts?.by_status?.completed || 0, '#3498DB'],
                  ['Upcoming', ts?.by_status?.upcoming || 0, '#D4A017'],
                ].map(([label, val, color]) => (
                  <div key={label} style={styles.snapshotCard}>
                    <div style={{ ...styles.snapshotValue, color }}>{val}</div>
                    <div style={styles.snapshotLabel}>{label}</div>
                  </div>
                ))}
              </div>

              {/* By Type & Department - compact chips */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                <div>
                  <h4 style={styles.tableTitle}>By Training Type</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(ts?.by_type || []).length > 0 ? ts.by_type.map((t) => (
                      <div key={t.training_type} style={styles.miniRow}>
                        <span style={styles.miniRowLabel}>{t.training_type}</span>
                        <span style={styles.miniRowValue}>{t.count}</span>
                      </div>
                    )) : (
                      <p style={styles.noData}>No data</p>
                    )}
                  </div>
                </div>
                <div>
                  <h4 style={styles.tableTitle}>By Department</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {(ts?.by_department || []).length > 0 ? ts.by_department.map((dep) => (
                      <div key={dep.department} style={styles.miniRow}>
                        <span style={styles.miniRowLabel}>{dep.department}</span>
                        <span style={styles.miniRowValue}>{dep.count}</span>
                      </div>
                    )) : (
                      <p style={styles.noData}>No data</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Process Completion Tiers */}
              <h4 style={styles.tableTitle}>Process Completion Summary</h4>
              <div style={styles.tierGrid}>
                {Object.entries(completionTiers).map(([tier, count]) => {
                  const colors = {
                    '0-25%': '#C0392B',
                    '26-50%': '#D4A017',
                    '51-75%': '#3498DB',
                    '76-100%': '#6B8063',
                  };
                  return (
                    <div key={tier} style={styles.tierCard}>
                      <div style={{ ...styles.tierBar, backgroundColor: colors[tier] }} />
                      <div style={{ ...styles.tierValue, color: colors[tier] }}>{count}</div>
                      <div style={styles.tierLabel}>{tier}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ═══════════════ PAGE 5: OJT SNAPSHOT ═══════════════ */}
            <div data-pdf-section="ojt" style={styles.sectionBlock}>
              <div style={styles.sectionHeader}>
                <div style={{ ...styles.sectionIcon, backgroundColor: '#3498DB' }}>04</div>
                <div>
                  <h2 style={styles.sectionTitle}>On Job Training</h2>
                  <p style={styles.sectionDesc}>Staff training records, cost, and OUL approval status</p>
                </div>
              </div>

              <div style={styles.snapshotGrid}>
                {[
                  ['Total Records', ojt?.total || 0, '#2B362E'],
                  ['OUL Approved', ojt?.oulApproved || 0, '#6B8063'],
                  ['Pending', (ojt?.total || 0) - (ojt?.oulApproved || 0), '#D4A017'],
                  ['Total Cost (SAR)', ojt?.totalCost ? Number(ojt.totalCost).toLocaleString() : '0', '#9B59B6'],
                ].map(([label, val, color]) => (
                  <div key={label} style={styles.snapshotCard}>
                    <div style={{ ...styles.snapshotValue, color }}>{val}</div>
                    <div style={styles.snapshotLabel}>{label}</div>
                  </div>
                ))}
              </div>

              {/* By Department */}
              {(ojt?.byDept || []).length > 0 && (
                <div>
                  <h4 style={styles.tableTitle}>By Department</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {ojt.byDept.map((dep) => (
                      <div key={dep.department} style={styles.miniRow}>
                        <span style={styles.miniRowLabel}>{dep.department}</span>
                        <span style={styles.miniRowValue}>{dep.count} records · SAR {Number(dep.total_cost).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ═══════════════ FOOTER ═══════════════ */}
            <div data-pdf-section="footer" style={styles.footer}>
              <div style={styles.footerLine} />
              <div style={styles.footerContent}>
                <div style={styles.footerLeft}>
                  <strong>NEOM Hospital</strong> Academic & Training Affairs Hub
                </div>
                <div style={styles.footerRight}>
                  Generated on {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
              <div style={styles.footerDisclaimer}>
                This report is confidential and intended for internal use only.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════
   Styles
   ════════════════════════════════════════════ */

const styles = {
  exportBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    backgroundColor: '#2B362E',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  exportBtnOutline: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    backgroundColor: '#ffffff',
    color: '#2B362E',
    border: '1px solid #d4d0c8',
    borderRadius: '8px',
    fontSize: '0.8rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  reportContainer: {
    backgroundColor: '#ffffff',
    padding: '0',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    maxWidth: '800px',
    margin: '0 auto',
  },

  /* Header */
  headerBar: {
    background: 'linear-gradient(135deg, #2B362E 0%, #3d4d40 100%)',
    padding: '18px 28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLogoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  headerIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'rgba(107, 128, 99, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontWeight: 800,
    fontSize: '0.7rem',
    letterSpacing: '1px',
  },
  headerTitle: {
    color: '#ffffff',
    fontSize: '0.95rem',
    fontWeight: 700,
    letterSpacing: '0.3px',
  },
  headerSubtitle: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.65rem',
    marginTop: '1px',
  },

  /* Report Title */
  reportTitleArea: {
    padding: '18px 28px 0',
    textAlign: 'center',
  },
  reportMainTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#2B362E',
    margin: '0 0 8px',
    letterSpacing: '-0.3px',
  },
  reportMeta: {
    display: 'flex',
    justifyContent: 'center',
    gap: '6px',
    flexWrap: 'wrap',
  },
  metaBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    backgroundColor: '#F5F0E8',
    borderRadius: '20px',
    fontSize: '0.65rem',
    color: '#6b6b6b',
    fontWeight: 500,
  },

  divider: {
    height: '1px',
    background: 'linear-gradient(to right, transparent, #d9d4cb, transparent)',
    margin: '14px 28px',
  },

  /* Summary Paragraph */
  summaryBox: {
    margin: '0 28px',
    padding: '14px 18px',
    backgroundColor: '#faf8f4',
    borderRadius: '8px',
    borderLeft: '3px solid #6B8063',
  },
  summaryTitle: {
    margin: '0 0 6px',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#2B362E',
  },
  summaryText: {
    margin: 0,
    fontSize: '0.7rem',
    lineHeight: 1.65,
    color: '#3a3a3a',
  },

  /* Executive Overview */
  execSection: {
    padding: '0 28px 20px',
  },
  execHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
  },
  execNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#2B362E',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '0.7rem',
  },
  execTitle: {
    margin: 0,
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#2B362E',
  },
  execDesc: {
    margin: '1px 0 0',
    fontSize: '0.65rem',
    color: '#B2AB9C',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
    marginBottom: '14px',
  },
  kpiCard: {
    padding: '10px 12px',
    borderRadius: '6px',
    backgroundColor: '#faf8f4',
    borderLeft: '3px solid #6B8063',
  },
  kpiValue: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: '#2B362E',
    lineHeight: 1.2,
  },
  kpiLabel: {
    fontSize: '0.62rem',
    color: '#6b6b6b',
    marginTop: '2px',
  },
  highlightBar: {
    backgroundColor: '#faf8f4',
    borderRadius: '6px',
    padding: '10px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  highlightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.68rem',
    color: '#2B362E',
  },
  highlightDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    flexShrink: 0,
  },

  /* Section Blocks */
  sectionBlock: {
    padding: '18px 28px 20px',
    borderTop: '1px solid #f0ece4',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '14px',
  },
  sectionIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: '#6B8063',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  sectionTitle: {
    margin: 0,
    fontSize: '0.88rem',
    fontWeight: 700,
    color: '#2B362E',
  },
  sectionDesc: {
    margin: '1px 0 0',
    fontSize: '0.62rem',
    color: '#B2AB9C',
  },

  /* Snapshot Grid */
  snapshotGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: '8px',
    marginBottom: '16px',
  },
  snapshotCard: {
    padding: '10px 8px',
    borderRadius: '6px',
    backgroundColor: '#faf8f4',
    textAlign: 'center',
    border: '1px solid #f0ece4',
  },
  snapshotValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  snapshotLabel: {
    fontSize: '0.58rem',
    color: '#6b6b6b',
    marginTop: '3px',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    fontWeight: 500,
  },

  /* Course Chips */
  courseChips: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '16px',
  },
  courseChip: {
    padding: '8px 12px',
    backgroundColor: '#faf8f4',
    borderRadius: '6px',
    border: '1px solid #f0ece4',
  },
  courseChipName: {
    fontWeight: 600,
    fontSize: '0.72rem',
    color: '#2B362E',
    marginBottom: '2px',
  },
  courseChipStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.62rem',
    color: '#6b6b6b',
    flexWrap: 'wrap',
  },
  chipDot: {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    backgroundColor: '#B2AB9C',
    display: 'inline-block',
  },

  /* Mini Row */
  miniRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 10px',
    backgroundColor: '#faf8f4',
    borderRadius: '4px',
    border: '1px solid #f0ece4',
  },
  miniRowLabel: {
    fontSize: '0.68rem',
    color: '#2B362E',
    fontWeight: 500,
  },
  miniRowValue: {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#2B362E',
  },

  /* Tables */
  tableTitle: {
    fontSize: '0.72rem',
    fontWeight: 600,
    color: '#2B362E',
    marginBottom: '8px',
    marginTop: '4px',
    paddingBottom: '4px',
    borderBottom: '2px solid #6B8063',
    display: 'inline-block',
  },

  /* Department Grid */
  deptGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
    gap: '6px',
    marginBottom: '16px',
  },
  deptCard: {
    padding: '8px',
    borderRadius: '6px',
    backgroundColor: '#faf8f4',
    textAlign: 'center',
    border: '1px solid #f0ece4',
  },
  deptCount: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#2B362E',
  },
  deptName: {
    fontSize: '0.58rem',
    color: '#6b6b6b',
    marginTop: '1px',
  },

  /* Tier cards */
  tierGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginBottom: '16px',
  },
  tierCard: {
    padding: '10px',
    borderRadius: '6px',
    backgroundColor: '#faf8f4',
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  tierBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
  },
  tierValue: {
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1.2,
  },
  tierLabel: {
    fontSize: '0.62rem',
    color: '#6b6b6b',
    marginTop: '2px',
    fontWeight: 500,
  },

  /* Empty notice */
  emptyNotice: {
    padding: '14px',
    backgroundColor: '#faf8f4',
    borderRadius: '6px',
    color: '#B2AB9C',
    fontStyle: 'italic',
    textAlign: 'center',
    fontSize: '0.68rem',
    marginBottom: '16px',
  },
  noData: {
    color: '#B2AB9C',
    fontStyle: 'italic',
    gridColumn: '1 / -1',
    textAlign: 'center',
    padding: '14px',
    fontSize: '0.68rem',
  },

  /* Footer */
  footer: {
    padding: '0 28px 18px',
    marginTop: '8px',
  },
  footerLine: {
    height: '2px',
    background: 'linear-gradient(to right, #6B8063, #3498DB, #9B59B6)',
    borderRadius: '2px',
    marginBottom: '10px',
  },
  footerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  footerLeft: {
    fontSize: '0.65rem',
    color: '#2B362E',
  },
  footerRight: {
    fontSize: '0.62rem',
    color: '#6b6b6b',
  },
  footerDisclaimer: {
    fontSize: '0.58rem',
    color: '#B2AB9C',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: '4px',
  },
};
